import { useEffect, useState } from 'react';
import { getWidgetSettings } from '../services/dataService';

declare global {
    interface Window {
        dataLayer: any[];
        gtag: (...args: any[]) => void;
    }
}

const GoogleAnalytics: React.FC = () => {
    const [gaId, setGaId] = useState<string | null>(null);
    const [enabled, setEnabled] = useState(false);
    const [cookieConsent, setCookieConsent] = useState<boolean | null>(null);

    useEffect(() => {
        const loadSettings = async () => {
            const settings = await getWidgetSettings();
            if (settings.googleAnalyticsEnabled && settings.googleAnalyticsId) {
                setGaId(settings.googleAnalyticsId);
                setEnabled(true);

                // Check if cookie consent is required
                if (settings.cookieConsentEnabled) {
                    const consent = localStorage.getItem('lumitours_cookie_consent');
                    setCookieConsent(consent === 'accepted');
                } else {
                    setCookieConsent(true); // No consent required
                }
            }
        };
        loadSettings();
    }, []);

    useEffect(() => {
        if (!enabled || !gaId || cookieConsent !== true) return;

        // Check if script already exists
        if (document.querySelector(`script[src*="googletagmanager.com/gtag/js"]`)) {
            return;
        }

        // Load gtag script
        const script = document.createElement('script');
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
        document.head.appendChild(script);

        // Initialize gtag
        window.dataLayer = window.dataLayer || [];
        window.gtag = function gtag() {
            window.dataLayer.push(arguments);
        };
        window.gtag('js', new Date());
        window.gtag('config', gaId, {
            anonymize_ip: true,
            cookie_flags: 'SameSite=None;Secure'
        });

        console.log('[GA] Google Analytics initialized with ID:', gaId);
    }, [enabled, gaId, cookieConsent]);

    return null;
};

// Cookie Consent Banner Component
export const CookieConsentBanner: React.FC = () => {
    const [show, setShow] = useState(false);
    const [settings, setSettings] = useState<any>(null);

    useEffect(() => {
        const loadSettings = async () => {
            const widgetSettings = await getWidgetSettings();
            setSettings(widgetSettings);

            if (widgetSettings.cookieConsentEnabled) {
                const consent = localStorage.getItem('lumitours_cookie_consent');
                if (!consent) {
                    setShow(true);
                }
            }
        };
        loadSettings();
    }, []);

    const handleAccept = () => {
        localStorage.setItem('lumitours_cookie_consent', 'accepted');
        setShow(false);
        window.location.reload(); // Reload to initialize GA
    };

    const handleDecline = () => {
        localStorage.setItem('lumitours_cookie_consent', 'declined');
        setShow(false);
    };

    if (!show || !settings?.cookieConsentEnabled) return null;

    return (
        <div className="fixed bottom-0 left-0 right-0 z-[9999] p-4 bg-gray-900/95 backdrop-blur-lg border-t border-gray-700 shadow-2xl">
            <div className="container mx-auto max-w-6xl flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex-1 text-gray-300 text-sm">
                    <p className="font-medium text-white mb-1">🍪 Vi använder cookies</p>
                    <p className="text-gray-400">
                        Vi använder cookies för att förbättra din upplevelse och analysera vår webbplatstrafik.
                        Genom att klicka på "Acceptera" godkänner du vår användning av cookies.
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <button
                        onClick={handleDecline}
                        className="px-4 py-2 text-gray-400 hover:text-white transition-colors text-sm"
                    >
                        Avvisa
                    </button>
                    <button
                        onClick={handleAccept}
                        className="px-6 py-2.5 bg-primary hover:bg-teal-600 text-white font-bold rounded-lg transition-all shadow-lg"
                    >
                        Acceptera
                    </button>
                </div>
            </div>
        </div>
    );
};

// Analytics tracking helper functions
export const trackPageView = (pagePath: string, pageTitle?: string) => {
    if (window.gtag) {
        window.gtag('event', 'page_view', {
            page_path: pagePath,
            page_title: pageTitle
        });
    }
};

export const trackEvent = (eventName: string, params?: Record<string, any>) => {
    if (window.gtag) {
        window.gtag('event', eventName, params);
    }
};

export const trackSearch = (searchTerm: string) => {
    trackEvent('search', { search_term: searchTerm });
};

export const trackHotelView = (hotelId: string, hotelName: string) => {
    trackEvent('view_item', {
        item_id: hotelId,
        item_name: hotelName,
        item_category: 'hotel'
    });
};

export const trackBookingStart = (hotelId: string, hotelName: string, price: number) => {
    trackEvent('begin_checkout', {
        item_id: hotelId,
        item_name: hotelName,
        value: price,
        currency: 'SEK'
    });
};

export const trackBookingComplete = (bookingId: string, hotelName: string, price: number) => {
    trackEvent('purchase', {
        transaction_id: bookingId,
        item_name: hotelName,
        value: price,
        currency: 'SEK'
    });
};

export default GoogleAnalytics;
